import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FiSmartphone, FiRefreshCw } from 'react-icons/fi';

const CAMPS = [
  { value:'', label:'All Campaigns' },
  { value:'medicare', label:'Medicare' },
  { value:'aca', label:'ACA' },
];
const S = {
  sel:{ padding:'8px 12px',border:'1px solid #d1d5db',borderRadius:'6px',fontSize:'14px',minWidth:'160px' },
  btn:{ display:'flex',alignItems:'center',padding:'8px 16px',background:'#1a56db',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'14px' },
  th:{ padding:'10px 14px',textAlign:'left',fontWeight:600,color:'#374151',borderBottom:'1px solid #e5e7eb',background:'#f9fafb',whiteSpace:'nowrap' },
  td:{ padding:'10px 14px',borderBottom:'1px solid #f3f4f6',fontSize:'13px' },
};
const bge=(color,txt)=><span style={{display:'inline-block',padding:'2px 8px',borderRadius:'12px',background:color+'20',color,fontWeight:600,fontSize:'12px'}}>{txt}</span>;
const platBadge=p=>p==='ios'?bge('#1a56db','📱 iOS'):p==='android'?bge('#16a34a','🤖 Android'):bge('#6b7280','🌐 Web/No App');
const fmt=d=>d?new Date(d).toLocaleDateString():'—';
const LIMIT = 50;

function AdminAppStats({ token }) {
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [campCounts, setCampCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [campaign, setCampaign] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [platFilter, setPlatFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const debounceRef = useRef(null);

  const fetchStats = useCallback(async (pg = 1, srch = search, plat = platFilter, camp = campaign) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT });
      if (camp)  params.set('campaign', camp);
      if (plat)  params.set('platform', plat);
      if (srch)  params.set('search', srch);
      const r=await axios.get(`/api/lead-portal/admin/app-stats?${params}`,{headers:{Authorization:`Bearer ${token}`}});
      setLeads(r.data.leads||[]);
      setSummary(r.data.summary||null);
      setCampCounts(r.data.campaignCounts||{});
      setPage(r.data.page||1);
      setTotalPages(r.data.totalPages||1);
      setTotal(r.data.total||0);
    } catch(e){console.error(e);}
    finally{setLoading(false);}
  }, [token]);

  useEffect(()=>{ fetchStats(1, search, platFilter, campaign); }, [campaign, platFilter]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      fetchStats(1, val, platFilter, campaign);
    }, 400);
  };

  const handlePlatChange = (e) => { setPlatFilter(e.target.value); };
  const handleCampChange = (e) => { setCampaign(e.target.value); };
  const goPage = (p) => { setPage(p); fetchStats(p, search, platFilter, campaign); };

  return (
    <div style={{padding:'24px'}}>
      <h1 style={{fontSize:'22px',fontWeight:700,marginBottom:'4px',display:'flex',alignItems:'center',gap:8}}>
        <FiSmartphone/>App Usage Stats — All Leads
      </h1>
      <p style={{color:'#6b7280',marginBottom:'16px'}}>Mobile app usage by campaign (iOS / Android / Web Portal).</p>

      {Object.keys(campCounts).length>0&&!campaign&&(
        <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:'10px',padding:'14px 18px',marginBottom:'16px'}}>
          <div style={{fontWeight:600,marginBottom:8,color:'#0369a1'}}>📊 Campaign Breakdown</div>
          <div style={{display:'flex',gap:'14px',flexWrap:'wrap'}}>
            {Object.entries(campCounts).map(([camp,c])=>(
              <div key={camp} style={{background:'#fff',border:'1px solid #e0f2fe',borderRadius:'8px',padding:'10px 16px'}}>
                <div style={{fontWeight:700,textTransform:'uppercase',color:'#0369a1',fontSize:'13px',marginBottom:4}}>{camp}</div>
                <div style={{fontSize:'13px',color:'#374151'}}>Total:<b>{c.total}</b> | App:<b style={{color:'#16a34a'}}>{c.hasApp}</b> | iOS:<b style={{color:'#7c3aed'}}>{c.ios}</b> | Android:<b style={{color:'#d97706'}}>{c.android}</b></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:'flex',gap:'12px',flexWrap:'wrap',marginBottom:'20px',alignItems:'center'}}>
        <select value={campaign} onChange={handleCampChange} style={S.sel}>
          {CAMPS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={platFilter} onChange={handlePlatChange} style={S.sel}>
          <option value="">All Platforms</option>
          <option value="ios">iOS Only</option>
          <option value="android">Android Only</option>
          <option value="none">No App</option>
        </select>
        <input placeholder="Search name/email/agent..." value={searchInput} onChange={handleSearchChange} style={{...S.sel,width:'240px'}}/>
        <button onClick={()=>fetchStats(1,search,platFilter,campaign)} style={S.btn} disabled={loading}>
          <FiRefreshCw style={{marginRight:4}}/>{loading?'Loading...':'Refresh'}
        </button>
      </div>

      {summary&&(
        <div style={{display:'flex',gap:'12px',flexWrap:'wrap',marginBottom:'24px'}}>
          {[['Total',summary.total,'#1a56db'],['Has App',summary.hasApp,'#16a34a'],
            ['iOS',summary.ios,'#7c3aed'],['Android',summary.android,'#d97706'],
            ['Web Portal',summary.web,'#0891b2'],['No App',summary.noApp,'#6b7280']
          ].map(([label,val,col])=>(
            <div key={label} style={{background:'#fff',border:'1px solid #e5e7eb',borderTop:`3px solid ${col}`,borderRadius:'10px',padding:'14px 20px',minWidth:'100px',textAlign:'center'}}>
              <div style={{fontSize:'24px',fontWeight:700,color:col}}>{val}</div>
              <div style={{fontSize:'12px',color:'#6b7280',marginTop:2}}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {loading?<div style={{textAlign:'center',padding:'40px',color:'#6b7280'}}>Loading...</div>:(
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',border:'1px solid #e5e7eb',fontSize:'13px'}}>
            <thead>
              <tr>{['Lead','Phone','Campaigns','Agent(s)','Platform','First Seen','Last Login'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {leads.length===0?(
                <tr><td colSpan={7} style={{textAlign:'center',padding:'32px',color:'#6b7280'}}>No leads found</td></tr>
              ):leads.map((l,i)=>(
                <tr key={l.leadEmail} style={{background:i%2===0?'#fff':'#f9fafb'}}>
                  <td style={S.td}>
                    <div style={{fontWeight:600,color:'#111827',fontSize:'13px'}}>{l.leadName||'—'}</div>
                    <div style={{fontSize:'11px',color:'#6b7280',marginTop:2}}>{l.leadEmail}</div>
                  </td>
                  <td style={S.td}>{l.leadPhone||'—'}</td>
                  <td style={S.td}>{(l.campaigns||[]).map(c=><span key={c} style={{display:'inline-block',padding:'2px 7px',borderRadius:'10px',background:'#1a56db18',color:'#1a56db',fontSize:'11px',fontWeight:600,marginRight:3,textTransform:'uppercase'}}>{c}</span>)}</td>
                  <td style={S.td}>{(l.agents||[]).map(a=>(
                    <div key={a.email} style={{marginBottom:4}}>
                      <div style={{fontWeight:600,color:'#111827',fontSize:'13px',whiteSpace:'nowrap'}}>{a.name||'—'}</div>
                      <div style={{fontSize:'11px',color:'#6b7280',whiteSpace:'nowrap'}}>{a.email}</div>
                    </div>
                  ))}</td>
                  <td style={S.td}>{platBadge(l.appPlatform)}</td>
                  <td style={S.td}>{fmt(l.appFirstSeen)}</td>
                  <td style={S.td}>{fmt(l.lastLogin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:10,flexWrap:'wrap',gap:8}}>
            <div style={{color:'#6b7280',fontSize:'13px'}}>
              Showing {leads.length} of {total} leads (page {page} of {totalPages})
            </div>
            {totalPages>1&&(
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>goPage(page-1)} disabled={page<=1||loading}
                  style={{...S.btn,background:page<=1?'#e5e7eb':'#1a56db',color:page<=1?'#9ca3af':'#fff',padding:'6px 12px',fontSize:13}}>
                  ← Prev
                </button>
                {[...Array(Math.min(5,totalPages))].map((_,i)=>{
                  const p=Math.max(1,Math.min(page-2,totalPages-4))+i;
                  return p>=1&&p<=totalPages?(
                    <button key={p} onClick={()=>goPage(p)}
                      style={{...S.btn,background:p===page?'#1a56db':'#f9fafb',color:p===page?'#fff':'#374151',padding:'6px 12px',fontSize:13,border:'1px solid #e5e7eb'}}>
                      {p}
                    </button>
                  ):null;
                })}
                <button onClick={()=>goPage(page+1)} disabled={page>=totalPages||loading}
                  style={{...S.btn,background:page>=totalPages?'#e5e7eb':'#1a56db',color:page>=totalPages?'#9ca3af':'#fff',padding:'6px 12px',fontSize:13}}>
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAppStats;
